/* screens/academy/AcademyConsole.jsx — модуль «Академия» в консоли /console/academy.
   Вкладки «Курсы» (методист) и «Проверка ДЗ» (куратор). Вёрстка на .acad-* (academy.css). */
import React from 'react';
import { PageHeader } from '../../components/patterns/PageHeader.jsx';
import { QueryBoundary, EmptyState } from '../../components/patterns/States.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Avatar } from '../../components/ui/Avatar.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { SearchInput } from '../../components/ui/Field.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import { RR } from '../../lib/router.jsx';
import * as api from '../../lib/api/academy.js';
import { CourseEditor } from './CourseEditor.jsx';
import { ReviewDrawer } from './ReviewDrawer.jsx';
import { OrdersTab } from './OrdersTab.jsx';
import { StudentsListTab } from './StudentsListTab.jsx';
import { RetakeQueue } from './RetakeRequests.jsx';
import { TabScroller } from './TabScroller.jsx';
import { ruDateToIso, isoToRuDate } from './ruDate.js';
// Лист консоли (PL-15 + классы волны 2). Импорт здесь, а не в main.jsx: стили
// нужны только методисту и куратору — ученику их грузить незачем.
import '../../styles/academy-console.css';

const { useState, useCallback, useEffect } = React;
const { useFakeQuery } = hooks;
const { useSearchParams } = RR;

// Размер страницы очереди ДЗ (CU-2). Очередь листается кнопкой «Показать ещё»:
// страницы НАКАПЛИВАЮТСЯ, поэтому обычный useFakeQuery (один ответ в state) не подходит.
const QUEUE_PAGE = 50;

const COURSE_TONE = { draft: 'neutral', published: 'accent', archived: 'neutral' };
const COURSE_LABEL = { draft: 'Черновик', published: 'Опубликован', archived: 'В архиве' };
const SUB_TONE = { submitted: 'accent', accepted: 'ok', revision: 'warn', rejected: 'bad' };
const SUB_LABEL = { submitted: 'На проверке', accepted: 'Принято', revision: 'На доработку', rejected: 'Отклонено' };

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/* Фильтр по дате в русском формате — разбор строки живёт в ruDate.js (там же
   объяснение, почему нативный type="date" в RU-продукте показывал mm/dd/yyyy). */
function RuDateFilter({ label, value, onChange }) {
  const [text, setText] = useState(isoToRuDate(value));
  // Значение сменилось СНАРУЖИ (кнопка «Сбросить») — подтягиваем текст под него.
  const [synced, setSynced] = useState(value);
  if (synced !== value) { setSynced(value); setText(isoToRuDate(value)); }
  // Ошибку показываем ПОСЛЕ ухода из поля: «03.08.202» на середине набора — это
  // ещё не ошибка, и красный ободок на каждом символе только мешает.
  const [bad, setBad] = useState(false);
  const type = (v) => {
    setText(v);
    setBad(false);
    const t = (v || '').trim();
    if (t === '') { onChange(''); return; }
    const iso = ruDateToIso(t);
    // Недобранную дату наружу не отдаём: иначе очередь перезапрашивалась бы на
    // каждом символе и мигала «ничего не найдено» посреди набора.
    if (iso) onChange(iso);
  };
  return (
    <label className="acad-filter">
      <span className="acad-filter__lbl">{label}</span>
      <input type="text" inputMode="numeric" maxLength={10} placeholder="дд.мм.гггг"
        className={'input' + (bad ? ' input--error' : '')} value={text}
        aria-invalid={bad || undefined} aria-label={`${label} (дд.мм.гггг)`}
        onChange={(e) => type(e.target.value)}
        onBlur={() => {
          const t = (text || '').trim();
          setBad(t !== '' && ruDateToIso(t) == null);
        }} />
      {bad && <span className="t-caption text-danger">Дата в формате дд.мм.гггг</span>}
    </label>
  );
}


// ── курсы (методист) ──────────────────────────────────────────────────────────
function CoursesTab({ q, openCourse, onCreate }) {
  const all = q.data || [];
  // Архив по умолчанию СКРЫТ. Курс уводят в архив именно затем, чтобы он перестал
  // мозолить глаза: отработавший поток, снятый с продажи тариф, остатки приёмки.
  // Пока консоль показывала все статусы подряд, список превращался в свалку, и
  // среди неё терялись живые курсы. Данные при этом целы — архив в один клик.
  const [showArchived, setShowArchived] = useState(false);
  const archivedCount = all.filter((c) => c.status === 'archived').length;
  const items = showArchived ? all : all.filter((c) => c.status !== 'archived');
  const published = all.filter((c) => c.status === 'published').length;
  const learners = items.reduce((s, c) => s + (c.enrollment_count || 0), 0);
  const lessons = items.reduce((s, c) => s + (c.lesson_count || 0), 0);

  return (
    <QueryBoundary loading={q.loading} error={q.error} empty={!all.length} onRetry={q.reload}
      emptyEl={<EmptyState icon="book" title="Курсов пока нет"
        body="Создайте первый курс и наполните его модулями и уроками."
        action={<Button variant="primary" iconLeft="plus" onClick={onCreate}>Новый курс</Button>} />}>
      {archivedCount > 0 && (
        <div className="acad-archive-toggle">
          <Button size="sm" variant={showArchived ? 'secondary' : 'ghost'}
            iconLeft={showArchived ? 'eye-off' : 'box'}
            onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? 'Скрыть архив' : `Показать архив (${archivedCount})`}
          </Button>
        </div>
      )}
      <div className="acad-stats">
        {/* Числительные согласуем с числом: «1 ученик», «2 ученика», «524 ученика».
            Раньше подпись была статичной и давала «1 учеников». */}
        <div className="acad-stat"><span className="acad-stat__val num">{published}</span><span className="acad-stat__key">опубликовано</span></div>
        <div className="acad-stat"><span className="acad-stat__val num">{learners}</span><span className="acad-stat__key">{api.studentsWord(learners)}</span></div>
        <div className="acad-stat"><span className="acad-stat__val num">{lessons}</span><span className="acad-stat__key">{api.lessonsWord(lessons)}</span></div>
      </div>
      <div className="acad-course-grid">
        {items.map((c) => (
          <button type="button" key={c.id} className="acad-course-card" onClick={() => openCourse(c.id)}>
            <div className="acad-course-card__cover">
              {c.cover_url ? <img src={c.cover_url} alt="" loading="lazy" /> : <Icon name="book" size={26} aria-hidden="true" />}
              <span className="acad-course-card__badge">
                <Pill tone={COURSE_TONE[c.status]} size="sm" glyph={false}>{COURSE_LABEL[c.status] || c.status}</Pill>
              </span>
            </div>
            <div className="acad-course-card__body">
              <div className="acad-course-card__title">{c.title}</div>
              {c.summary && <div className="acad-course-card__summary">{c.summary}</div>}
              <div className="acad-kpis">
                <span className="acad-kpi"><span className="num">{c.module_count}</span> {api.modulesWord(c.module_count)}</span>
                <span className="acad-kpi"><span className="num">{c.lesson_count}</span> {api.lessonsWord(c.lesson_count)}</span>
                <span className="acad-kpi"><span className="num">{c.enrollment_count}</span> {api.studentsWord(c.enrollment_count)}</span>
                {/* AD-15: заявки со страницы продаж. Показываем ТОЛЬКО когда они
                    есть: у курса без формы заявок их не бывает никогда, и вечный
                    «0 заявок» на каждой карточке — шум, в котором теряется живое
                    число. */}
                {c.lead_count > 0 && (
                  <span className="acad-kpi"><span className="num">{c.lead_count}</span> {api.leadsWord(c.lead_count)}</span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </QueryBoundary>
  );
}

// ── проверка ДЗ (куратор) ───────────────────────────────────────────────────────
const REVIEW_FILTERS = [
  { value: 'submitted', label: 'На проверке' },
  { value: 'accepted', label: 'Принятые' },
  { value: 'revision', label: 'На доработке' },
  { value: 'all', label: 'Все' },
];

/* Очередь ДЗ постранично (CU-2). Курсор {key, offset} — ОДНО состояние: смена фильтров
   и офсета атомарна, иначе первый прогон эффекта дозагрузил бы вторую страницу старого
   фильтра поверх нового списка. Ответ терпит оба контракта — плоский массив и
   {count,results} (api.normalizeQueue). */
function useReviewQueue(filters, bump) {
  const [nonce, setNonce] = useState(0);
  const key = JSON.stringify(filters) + '|' + bump + '|' + nonce;
  const [cursor, setCursor] = useState({ key, offset: 0 });
  const [state, setState] = useState({ items: [], count: 0, loading: true, error: null, paginated: false });
  // фильтры сменились → возвращаемся на первую страницу ещё до эффекта загрузки
  if (cursor.key !== key) setCursor({ key, offset: 0 });

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    api.getReviewQueue({ ...filters, limit: QUEUE_PAGE, offset: cursor.offset })
      .then((data) => {
        if (!alive) return;
        const page = api.normalizeQueue(data);
        setState((s) => ({
          items: cursor.offset === 0 ? page.items : [...s.items, ...page.items],
          count: page.count, loading: false, error: null, paginated: page.paginated,
        }));
      })
      .catch((err) => {
        if (alive) setState((s) => ({ ...s, loading: false, error: { status: err.status || 500, message: err.message } }));
      });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [cursor.key, cursor.offset]);

  return {
    ...state,
    // «Показать ещё» только если бэк реально страничный и хвост не исчерпан
    hasMore: state.paginated && state.items.length < state.count,
    loadMore: () => setCursor((c) => ({ ...c, offset: c.offset + QUEUE_PAGE })),
    reload: () => setNonce((n) => n + 1),
  };
}

/* Фильтры очереди живут В РОДИТЕЛЕ (AcademyConsole), а не здесь: конвейер проверки
   (CU-3) подбирает следующую работу тем же набором фильтров, а дровер — сосед
   этой вкладки, не её потомок. Держать фильтры внизу значило бы вести куратора
   по всей очереди, хотя он отбирал работы одного курса. */
function ReviewTab({ openReview, openCourse, bump, pending, filters, onFilter }) {
  const filter = filters.status;
  const coursesQ = useFakeQuery('academy-review-courses', () => api.getReviewCourses(), []);
  const reviewCourses = coursesQ.data || [];
  const q = useReviewQueue(filters, bump);
  const items = q.items;
  const hasFilters = filters.course_id || filters.submitted_after || filters.reviewed_after || filters.q;
  const search = filters.q;
  const setSearch = (v) => onFilter({ q: v });
  const reset = () => onFilter({ course_id: '', submitted_after: '', reviewed_after: '', q: '' });

  return (
    <>
      <TabScroller>
        <div className="acad-tabs" role="tablist" aria-label="Фильтр проверки">
          {REVIEW_FILTERS.map((f) => (
            <button key={f.value} role="tab" aria-selected={filter === f.value}
              className={'acad-seg' + (filter === f.value ? ' is-active' : '')}
              onClick={() => onFilter({ status: f.value })}>
              {f.label}
              {f.value === 'submitted' && pending > 0 && <span className="acad-seg__count num">{pending}</span>}
            </button>
          ))}
        </div>
      </TabScroller>
      <div className="acad-filters">
        <label className="acad-filter ord-search">
          <span className="acad-filter__lbl">Поиск ученика</span>
          <SearchInput value={search} onDebouncedChange={setSearch}
            placeholder="ФИО, email или телефон" />
        </label>
        <label className="acad-filter">
          <span className="acad-filter__lbl">Курс</span>
          {/* CU-6: число непроверенных работ рядом с курсом — бэк его считает
              (ReviewCoursesView.pending), а выпадашка показывала голые названия,
              и выбрать «где горит» было нельзя. */}
          <select className="input" value={filters.course_id}
            onChange={(e) => onFilter({ course_id: e.target.value })}>
            <option value="">Все курсы</option>
            {reviewCourses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}{c.pending ? ` — на проверке: ${c.pending}` : ''}
              </option>
            ))}
          </select>
          {/* Рядом уживаются ДВА числа: «Проверка ДЗ N» в шапке — это вся очередь,
              «на проверке: N» у курса — только его доля. Пока это не подписано,
              расхождение читалось как ошибка в данных, и куратор не понимал,
              какому числу верить. */}
          <span className="t-caption text-mute">
            «на проверке» — работы этого курса; в шапке вкладки — вся очередь
          </span>
          {/* CU-6: единственный вход куратора в аналитику курса. Вкладки «Курсы»
              у него нет (она под правом методиста), и до недавнего времени
              открыть карточку курса из интерфейса было НЕЧЕМ — аналитика жила
              только по вручную набранному адресу с id. Требование «куратор видит,
              кто отваливается» адресовано ему, значит и вход обязан быть у него. */}
          {filters.course_id && openCourse && (
            <Button size="sm" variant="secondary" iconLeft="analytics"
              onClick={() => openCourse(filters.course_id)}>
              Аналитика курса
            </Button>
          )}
        </label>
        <RuDateFilter label="Сдано после" value={filters.submitted_after}
          onChange={(v) => onFilter({ submitted_after: v })} />
        <RuDateFilter label="Проверено после" value={filters.reviewed_after}
          onChange={(v) => onFilter({ reviewed_after: v })} />
        {hasFilters && <Button variant="ghost" size="sm" iconLeft="close" onClick={reset}>Сбросить</Button>}
      </div>
      <QueryBoundary loading={q.loading && !items.length} error={q.error} empty={!items.length} onRetry={q.reload}
        emptyEl={search
          ? <EmptyState serif={false} icon="search" title="Ничего не нашлось"
              body="По этому запросу работ нет. Измените или очистите поиск."
              action={<Button variant="secondary" iconLeft="close" onClick={() => setSearch('')}>Очистить поиск</Button>} />
          : <EmptyState icon="check-circle" title="Очередь пуста"
              body="Здесь появятся домашние задания на проверку." />}>
        <div className="ord-count t-caption text-mute">
          Показано <span className="num">{items.length}</span> из <span className="num">{q.count}</span>
          {' — по выбранным фильтрам'}
        </div>
        <div className="acad-queue" role="list">
          {items.map((s) => (
            <button type="button" key={s.id} role="listitem"
              className={'acad-row' + (s.status === 'submitted' ? ' is-unread' : '')}
              onClick={() => openReview(s.id)}>
              <Avatar label={s.student_label || 'Ученик'} size={36} />
              <span className="acad-row__main">
                <span className="acad-row__name">{s.student_label || 'Ученик'}</span>
                {/* Работы курса, ушедшего в архив/черновик, из очереди не убираем:
                    ученик уже сдал, и проверку надо довести. Но без пометки строка
                    врёт — куратор ищет этот курс в списке и не находит. */}
                <span className="acad-row__sub">
                  {s.course_title}
                  {s.course_status && s.course_status !== 'published'
                    ? ` (${COURSE_LABEL[s.course_status] || s.course_status})` : ''}
                  {' · '}{s.lesson_title}
                </span>
              </span>
              <Pill tone={SUB_TONE[s.status]} size="sm" glyph={false}>{SUB_LABEL[s.status] || s.status}</Pill>
              <time className="acad-row__time" dateTime={s.created_at}>{fmt(s.created_at)}</time>
            </button>
          ))}
        </div>
        {q.hasMore && (
          <div className="cluster cluster-8" style={{ justifyContent: 'center', marginTop: 'var(--space-8)' }}>
            <Button variant="secondary" size="sm" iconLeft="chevron-down"
              loading={q.loading} onClick={q.loadMore}>Показать ещё</Button>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}

const REVIEW_FILTERS_INIT = {
  status: 'submitted', course_id: '', submitted_after: '', reviewed_after: '', q: '',
};

function AcademyConsole() {
  const { user } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [bump, setBump] = useState(0);
  const [reviewFilters, setReviewFilters] = useState(REVIEW_FILTERS_INIT);
  const canManage = !!user?.academy_manage;
  const canReview = !!user?.academy_curate;
  const onReviewFilter = useCallback((patch) => setReviewFilters((f) => ({ ...f, ...patch })), []);

  const coursesQ = useFakeQuery('acad-courses-' + bump,
    () => (canManage ? api.listCourses() : Promise.resolve([])), [bump]);
  // Счётчик на табе — из count пагинации, а не из длины страницы: на длине он залипал
  // на срезе бэка (500) и врал, сколько работ реально ждёт куратора (CU-2). Просим
  // limit=1: нужен только счётчик, сами строки грузит ReviewTab.
  const pendingQ = useFakeQuery('acad-pending-' + bump,
    () => (canReview ? api.getReviewQueue({ status: 'submitted', limit: 1 }) : Promise.resolve([])), [bump]);
  // ST-5: заявки на пересдачу — рабочее место рядом с очередью ДЗ. Без счётчика
  // на табе заявка ученика лежала бы незамеченной: тупик «попытки исчерпаны»
  // держит и все следующие обязательные уроки (sequential-лок).
  const retakesQ = useFakeQuery('acad-retakes-count-' + bump,
    () => (canReview ? api.getRetakeRequests() : Promise.resolve([])), [bump]);
  const courses = coursesQ.data || [];
  const pending = api.normalizeQueue(pendingQ.data).count;
  const retakes = Array.isArray(retakesQ.data) ? retakesQ.data.length : 0;
  /* Счётчик таба до ответа сервера. Раньше на первом кадре он рисовал «0»: пустая
     очередь и НЕзагруженная очередь выглядели одинаково, и куратор уходил с
     экрана, решив, что заявок нет. `null` PageHeader не рисует вовсе (t.count != null),
     то есть число появляется ровно тогда, когда оно известно. */
  const pendingCount = pendingQ.data == null ? null : pending;
  const retakesCount = retakesQ.data == null ? null : retakes;

  const reloadAll = useCallback(() => setBump((n) => n + 1), []);
  const setParam = (k, v) => setParams((prev) => {
    const sp = new URLSearchParams(prev);
    if (v == null) sp.delete(k); else sp.set(k, String(v));
    return sp;
  });

  const tabs = [];
  if (canManage) tabs.push({ id: 'courses', label: 'Курсы', count: coursesQ.data == null ? null : courses.length });
  if (canReview) tabs.push({ id: 'review', label: 'Проверка ДЗ', count: pendingCount });
  if (canReview) tabs.push({ id: 'retakes', label: 'Пересдачи', count: retakesCount });
  if (canManage) tabs.push({ id: 'students', label: 'Ученики' });
  if (canManage) tabs.push({ id: 'orders', label: 'Заказы' });
  const tab = params.get('tab') || tabs[0]?.id || 'courses';

  const create = useCallback(async () => {
    try {
      const c = await api.createCourse({ title: 'Новый курс' });
      reloadAll();
      setParam('course', c.id);
    } catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
    // eslint-disable-next-line
  }, [reloadAll]);

  const courseId = params.get('course');
  const reviewId = params.get('review');
  const closeDrawer = (key) => (changed) => { setParam(key, null); if (changed) reloadAll(); };

  return (
    <div className="section-page">
      <PageHeader breadcrumbs={[{ label: 'Админка', to: '/console' }, { label: 'Академия' }]}
        title="Академия" tabs={tabs} activeTab={tab} onTab={(id) => setParam('tab', id)}
        actions={canManage && tab === 'courses'
          ? <Button variant="primary" size="sm" iconLeft="plus" onClick={create}>Новый курс</Button>
          : null} />
      <div className="section-body">
        {tab === 'review'
          ? <ReviewTab openReview={(id) => setParam('review', id)}
              openCourse={(id) => setParam('course', id)}
              bump={bump} pending={pending}
              filters={reviewFilters} onFilter={onReviewFilter} />
          : tab === 'retakes' && canReview
            ? <RetakeQueue key={'retakes-' + bump} onChanged={reloadAll} />
          : tab === 'students' && canManage
            ? <StudentsListTab />
            : tab === 'orders' && canManage
              ? <OrdersTab />
              : <CoursesTab q={{ ...coursesQ, reload: reloadAll }} openCourse={(id) => setParam('course', id)} onCreate={create} />}
      </div>

      {/* canManage прокидываем явно: у куратора дровер обязан показать ровно те
          вкладки, которые ему откроются, а не весь набор с отказом внутри. */}
      {courseId && <CourseEditor courseId={Number(courseId)} canManage={canManage}
        onClose={closeDrawer('course')} />}
      {/* CU-3: дровер проверки работает конвейером — после вердикта сам переходит
          на следующую непроверенную работу в ТЕХ ЖЕ фильтрах очереди (status для
          подбора всегда 'submitted', сегмент списка на это не влияет). */}
      {reviewId && <ReviewDrawer submissionId={Number(reviewId)} onClose={closeDrawer('review')}
        queueFilters={{ course_id: reviewFilters.course_id, q: reviewFilters.q,
          submitted_after: reviewFilters.submitted_after }}
        onAdvance={(id) => setParam('review', id)} />}
    </div>
  );
}

export { AcademyConsole };
